import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FieldRenderer } from "@/components/shared/fields/FieldRenderer";
import { useFieldRegistry } from "@/hooks/admin/useFieldRegistry";
import type { FieldDefinition } from "@/types/fieldRegistry";

const PRIMARY_FIELD_NAMES = new Set([
  'sobrenome',
  'faturamento_mensal',
  'capital_de_investimento_inicial',
  'area_de_atuacao',
  'servico_de_interesse',
  'unidade',
  'link_linkedin',
  'desafio_comercial',
  'ativar_fluxo',
]);

const EXCLUDED_FIELD_NAMES = new Set([
  'ativar_no_fluxo',       // duplicata de ativar_fluxo
  'link_do_linkedin',      // duplicata de link_linkedin
  'id_contato_hubspot_2',  // duplicata de id_contato_hubspot
]);

interface LeadCustomFieldsSectionProps {
  customData?: Record<string, any>;
  onCustomDataChange?: (data: Record<string, any>) => void;
}

export function LeadCustomFieldsSection({
  customData = {},
  onCustomDataChange,
}: LeadCustomFieldsSectionProps) {
  const { fields, isLoading } = useFieldRegistry('leads');
  const [primaryOpen, setPrimaryOpen] = useState(true);
  const [secondaryOpen, setSecondaryOpen] = useState(false);

  const handleChange = (fieldName: string, value: any) => {
    const next = { ...customData, [fieldName]: value };
    onCustomDataChange?.(next);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-4 bg-muted animate-pulse rounded" />
        <div className="h-10 bg-muted animate-pulse rounded" />
        <div className="h-4 bg-muted animate-pulse rounded" />
        <div className="h-10 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  // Filtrar, deduplicar e dividir campos
  const seen = new Set<string>();
  const uniqueFields = fields.filter((f) => {
    if (f.is_system) return false;
    if (!f.is_visible_in_add_form) return false;
    // Calculado não se digita: o valor nasce do trigger ao salvar o lead.
    if (f.field_type === 'calculated') return false;
    if (EXCLUDED_FIELD_NAMES.has(f.field_name)) return false;
    if (seen.has(f.field_name)) return false;
    seen.add(f.field_name);
    return true;
  });

  const primaryFields = uniqueFields.filter((f) => PRIMARY_FIELD_NAMES.has(f.field_name));
  const secondaryFields = uniqueFields.filter((f) => !PRIMARY_FIELD_NAMES.has(f.field_name));

  if (primaryFields.length === 0 && secondaryFields.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
        <p className="text-sm">Nenhum campo configurado</p>
        <p className="text-xs mt-1">Configure campos nas configurações da organização</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {primaryFields.length > 0 && (
        <Collapsible open={primaryOpen} onOpenChange={setPrimaryOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-between px-2 py-1.5 h-auto font-medium text-sm hover:bg-accent/50"
            >
              <div className="flex items-center gap-2">
                {primaryOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <span>Campos principais</span>
              </div>
              <span className="text-xs text-muted-foreground">{primaryFields.length} campos</span>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-2 pl-6">
              <FieldList fields={primaryFields} customData={customData} onChange={handleChange} />
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {secondaryFields.length > 0 && (
        <Collapsible open={secondaryOpen} onOpenChange={setSecondaryOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-between px-2 py-1.5 h-auto font-medium text-sm hover:bg-accent/50"
            >
              <div className="flex items-center gap-2">
                {secondaryOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <span>Campos complementares</span>
              </div>
              <span className="text-xs text-muted-foreground">{secondaryFields.length} campos</span>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-2 pl-6">
              <FieldList fields={secondaryFields} customData={customData} onChange={handleChange} />
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

function FieldList({
  fields,
  customData,
  onChange,
}: {
  fields: FieldDefinition[];
  customData: Record<string, any>;
  onChange: (fieldName: string, value: any) => void;
}) {
  return (
    <div className="space-y-3">
      {fields.map((field) => {
        const value = customData?.[field.field_name] ?? null;

        // Guard futuro para tipo boolean (não ativado atualmente — FieldType não inclui 'boolean')
        if ((field.field_type as string) === 'boolean') {
          return (
            <div key={field.id} className="flex items-center gap-3">
              <Switch
                id={field.field_name}
                checked={value === 'true' || value === true}
                onCheckedChange={(checked) => onChange(field.field_name, String(checked))}
              />
              <Label htmlFor={field.field_name}>
                {field.field_label}
                {field.is_required && <span className="text-destructive ml-1">*</span>}
              </Label>
            </div>
          );
        }

        return (
          <div key={field.id} className="space-y-1.5">
            <label className="text-sm font-medium leading-none">
              {field.field_label}
              {field.is_required && <span className="text-destructive ml-1">*</span>}
              {field.is_important && <span className="text-amber-500 ml-1">*</span>}
            </label>
            <FieldRenderer
              field={field}
              value={value}
              onChange={(val) => onChange(field.field_name, val)}
            />
          </div>
        );
      })}
    </div>
  );
}
